import { FieldValueTransformer } from './types';
import { findDuplicatesOn } from './utils';

/**
 * Represents a schema definition for an entire Schema() decorated class. Acts as a container for all attribute definitions
 * with functionality provided to access and validate them.
 */
export class SchemaDefinition {
	private _attrs: { [attr: string]: AttributeDefinition } = {};
	public errors: string[] = [];

	constructor(
		public name: string,
		data: { [attr: string]: Definition[] }
	) {
		for (const [attr, defs] of Object.entries(data)) {
			this._attrs[attr] = new AttributeDefinition(attr, defs);
		}
	}

	attr(name: string) {
		return this._attrs[name];
	}

	attrs() {
		return Object.values(this._attrs);
	}

	isValid() {
		const pks = this.attrs().filter((attr) => attr.isPk()).length;

		if (pks > 1) {
			this.errors.push(`Multiple primary keys defined for schema: ${this.name}`);
		}

		this.errors.push(...this.attrs().flatMap((attr) => attr.validate()));

		return !this.errors.length;
	}
}

class AttributeDefinition {
	constructor(
		public readonly name: string,
		private _defs: Definition[]
	) {}

	isField() {
		return !!this.field;
	}

	isPk() {
		return !!this.pk;
	}

	isOptional() {
		return !!this.optional;
	}

	get field() {
		return this.getDef(FieldDefinition);
	}

	get from() {
		return this.getDef(FromDefinition);
	}

	get pk() {
		return this.getDef(PkDefinition);
	}

	get optional() {
		return this.getDef(OptionalDefinition);
	}

	validate() {
		const errors = [];

		const duplicates = findDuplicatesOn(this._defs, (def) => def.constructor.name);

		if (duplicates.length > 0) {
			errors.push(`Field ${this.name} contains multiple definitions: ${duplicates.join(', ')}`);
		}

		if (!this.isField()) {
			errors.push(`No field definition found for ${this.name}`);
		}

		return errors;
	}

	private getDef<T>(ctor: new (...args: any[]) => T): T {
		return this._defs.find((def) => def instanceof ctor) as T;
	}
}

class Definition {}

function returnSelf(n: any) {
	return n;
}

export class FieldDefinition extends Definition {
	constructor(public transformer: FieldValueTransformer = returnSelf) {
		super();
	}
}

export class FromDefinition extends Definition {
	constructor(public name: string) {
		super();
	}
}

export class PkDefinition extends Definition {}

export class OptionalDefinition extends Definition {}
